// The chat page's streaming state machine: exchange list, session hydration,
// the live SSE consumer, and the plan-approval / clarification resume flows.
// Chat.tsx keeps layout; this hook keeps everything that mutates state.

import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import type { PendingAttachment } from "@/components/chat/ChatComposer";
import { eventToThinkingEntry } from "@/components/chat/thinkingEvents";
import type { ThinkingEntry } from "@/components/chat/ThinkingPanel";
import { getSessionTurns } from "@/services/api/sessions";
import { askChatStream, ChatStreamEvent } from "@/services/chat/streamService";
import type {
  AnswerEventData,
  Artifact,
  Citation,
  ClarificationRequestedEventData,
  PlanStep,
} from "@/types/eryndal";

export interface ChatExchange {
  id: string;
  query: string;
  answer: string;
  citations: Citation[];
  artifacts: Artifact[];
  thinking: ThinkingEntry[];
  groundingScore: number | null;
  isStreaming: boolean;
  startedAt: number;
}

export interface PendingPlan {
  plan: PlanStep[];
  rationale?: string;
  answerMode?: string;
  exchangeId: string;
}

export interface PendingClarification {
  payload: ClarificationRequestedEventData;
  exchangeId: string;
}

export function useChatStream() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const requestedSession = searchParams.get("session");
  const [sessionId, setSessionId] = useState<string | null>(requestedSession);
  const [exchanges, setExchanges] = useState<ChatExchange[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [hydrating, setHydrating] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // v2 Step 4: pending plan awaiting user approval.
  const [pendingPlan, setPendingPlan] = useState<PendingPlan | null>(null);

  // v2 Step 5: pending sub-agent clarification awaiting user reply.
  const [pendingClarification, setPendingClarification] =
    useState<PendingClarification | null>(null);

  // Hydrate / reset when the URL session param changes.
  useEffect(() => {
    let cancelled = false;
    if (!requestedSession) {
      setSessionId(null);
      setExchanges([]);
      return;
    }
    setSessionId(requestedSession);
    setHydrating(true);
    getSessionTurns(requestedSession)
      .then((turns) => {
        if (cancelled) return;
        const hydrated: ChatExchange[] = turns.map((t) => {
          const startedAt = t.created_at
            ? new Date(t.created_at).getTime()
            : Date.now();
          const thinking: ThinkingEntry[] = (t.thinking_events ?? [])
            .map((evt, idx) => {
              const ts = startedAt + (evt.elapsed_ms ?? idx * 10);
              return eventToThinkingEntry(
                evt.kind,
                evt.data ?? {},
                ts,
                `${requestedSession}-${t.turn_index}-${idx}`,
              );
            })
            .filter((e): e is ThinkingEntry => e !== null);
          return {
            id: `${requestedSession}-${t.turn_index}`,
            query: t.query,
            answer: t.answer_summary ?? "",
            citations: t.citations ?? [],
            artifacts: t.artifacts ?? [],
            thinking,
            groundingScore: t.grounding_score ?? null,
            isStreaming: false,
            startedAt,
          };
        });
        setExchanges(hydrated);
      })
      .catch(() => {
        if (!cancelled) setExchanges([]);
      })
      .finally(() => {
        if (!cancelled) setHydrating(false);
      });
    return () => {
      cancelled = true;
    };
    // location.key changes on every router navigation, even when the URL
    // is the same — that lets "+ New chat" reset the welcome state when the
    // user is already on /chat.
  }, [requestedSession, location.key]);

  const updateExchange = useCallback((id: string, patch: Partial<ChatExchange>) => {
    setExchanges((prev) => prev.map((ex) => (ex.id === id ? { ...ex, ...patch } : ex)));
  }, []);

  const appendThinking = useCallback((id: string, entry: ThinkingEntry) => {
    setExchanges((prev) =>
      prev.map((ex) =>
        ex.id === id ? { ...ex, thinking: [...ex.thinking, entry] } : ex,
      ),
    );
  }, []);

  const ask = useCallback(
    async (query: string, attachments: PendingAttachment[]) => {
      if (isStreaming) return;
      const id = crypto.randomUUID();
      const startedAt = Date.now();
      const exchange: ChatExchange = {
        id,
        query,
        answer: "",
        citations: [],
        artifacts: [],
        thinking: [],
        groundingScore: null,
        isStreaming: true,
        startedAt,
      };
      setExchanges((prev) => [...prev, exchange]);
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      let counter = 0;
      const nextId = () => `${id}-${++counter}`;

      try {
        for await (const event of askChatStream({
          query,
          sessionId,
          attachments: attachments.map((a) => a.id),
          forceClass: searchParams.get("force_class") || null,
          signal: controller.signal,
          getAccessToken: () => getAccessTokenSilently().catch(() => null),
        })) {
          const ts = Date.now();

          if (event.kind === "draft_delta") {
            const data = event.data as { text?: string };
            if (data.text) {
              setExchanges((prev) =>
                prev.map((ex) =>
                  ex.id === id ? { ...ex, answer: ex.answer + data.text! } : ex,
                ),
              );
            }
          } else if (event.kind === "draft") {
            const data = event.data as { summary?: string };
            if (data.summary) updateExchange(id, { answer: data.summary });
            const entry = eventToThinkingEntry(
              event.kind,
              (event.data ?? {}) as Record<string, unknown>,
              ts,
              nextId(),
            );
            if (entry) appendThinking(id, entry);
          } else if (event.kind === "answer") {
            const data = event.data as AnswerEventData;
            updateExchange(id, {
              answer: data.answer.summary ?? "",
              citations: data.answer.citations ?? [],
              artifacts: data.answer.artifacts ?? [],
              groundingScore: data.grounding_score ?? null,
              isStreaming: false,
            });
            // Stop the thinking-process timer as soon as the answer is in
            // hand. The SSE stream stays open for residual cleanup
            // events (spend recorder, audit trail, rolling summary) but
            // the user has the answer — keeping the timer ticking
            // through that tail makes the UI look frozen-on-thinking.
            setIsStreaming(false);
            if (data.session_id && data.session_id !== sessionId) {
              setSessionId(data.session_id);
              queryClient.invalidateQueries({ queryKey: ["sessions"] });
            }
          } else if (event.kind === "plan_proposed") {
            const data = event.data as {
              plan?: PlanStep[];
              rationale?: string;
              answer_mode?: string;
            };
            if (data.plan?.length) {
              setPendingPlan({
                plan: data.plan,
                rationale: data.rationale,
                answerMode: data.answer_mode,
                exchangeId: id,
              });
              // Stream closes server-side after plan_proposed; stop streaming indicator.
              updateExchange(id, { isStreaming: false });
              setIsStreaming(false);
            }
          } else if (event.kind === "clarification_requested") {
            // v2 Step 5: a sub-agent paused with ask_user. Render the
            // ClarificationCard inline; the answer POSTs to /clarify and
            // re-opens the SSE stream into the same exchange.
            const data = event.data as ClarificationRequestedEventData;
            if (data.question) {
              setPendingClarification({ payload: data, exchangeId: id });
              updateExchange(id, { isStreaming: false });
              setIsStreaming(false);
            }
          } else {
            // Everything else is a thinking-panel row. The shared mapper is
            // the single source of truth, so live turns render exactly like
            // hydrated ones.
            const entry = eventToThinkingEntry(
              event.kind,
              (event.data ?? {}) as Record<string, unknown>,
              ts,
              nextId(),
            );
            if (entry) appendThinking(id, entry);
          }
        }
      } catch (err) {
        appendThinking(id, {
          id: nextId(),
          kind: "error",
          label: err instanceof Error ? err.message : "Stream failed",
          timestamp: Date.now(),
        });
      } finally {
        abortRef.current = null;
        // If a plan_proposed event paused the stream, streaming flags were
        // already cleared inside the plan_proposed branch above.
        // Only clear them here on natural (non-paused) stream end.
        setExchanges((prev) => {
          const ex = prev.find((e) => e.id === id);
          if (ex?.isStreaming) {
            setIsStreaming(false);
            return prev.map((e) => (e.id === id ? { ...e, isStreaming: false } : e));
          }
          return prev;
        });
        // §B-3b: refresh the session attachment chip strip — uploads
        // attached on this turn just got indexed into the table.
        queryClient.invalidateQueries({
          queryKey: ["chat", "sessions", sessionId, "attachments"],
        });
      }
    },
    // searchParams matters: `ask` reads force_class out of the URL, which the
    // sidebar's Routing dropdown writes. Without it here the callback keeps a
    // stale copy and the override the user just picked is not applied.
    [
      appendThinking,
      getAccessTokenSilently,
      isStreaming,
      queryClient,
      searchParams,
      sessionId,
      updateExchange,
    ],
  );

  const consumeResumeStream = useCallback(
    async (
      stream: AsyncGenerator<ChatStreamEvent>,
      exchangeId: string,
      idPrefix: string,
      errorLabel: string,
    ) => {
      let counter = 0;
      const nextId = () => `${exchangeId}-${idPrefix}-${++counter}`;
      try {
        for await (const event of stream) {
          const ts = Date.now();
          if (event.kind === "answer") {
            const data = event.data as AnswerEventData;
            updateExchange(exchangeId, {
              answer: data.answer.summary ?? "",
              citations: data.answer.citations ?? [],
              artifacts: data.answer.artifacts ?? [],
              groundingScore: data.grounding_score ?? null,
            });
            if (data.session_id && data.session_id !== sessionId) {
              setSessionId(data.session_id);
              queryClient.invalidateQueries({ queryKey: ["sessions"] });
            }
          } else if (event.kind === "draft_delta") {
            const data = event.data as { text?: string };
            if (data.text) {
              setExchanges((prev) =>
                prev.map((ex) =>
                  ex.id === exchangeId ? { ...ex, answer: ex.answer + data.text! } : ex,
                ),
              );
            }
          } else if (event.kind === "clarification_requested") {
            // Cascading clarifications — sub-agent asked again.
            const data = event.data as ClarificationRequestedEventData;
            if (data.question) {
              setPendingClarification({ payload: data, exchangeId });
              updateExchange(exchangeId, { isStreaming: false });
              setIsStreaming(false);
              return;
            }
          } else {
            const entry = eventToThinkingEntry(
              event.kind,
              (event.data ?? {}) as Record<string, unknown>,
              ts,
              nextId(),
            );
            if (entry) appendThinking(exchangeId, entry);
          }
        }
      } catch (err) {
        appendThinking(exchangeId, {
          id: nextId(),
          kind: "error",
          label: err instanceof Error ? err.message : errorLabel,
          timestamp: Date.now(),
        });
      } finally {
        updateExchange(exchangeId, { isStreaming: false });
        setIsStreaming(false);
      }
    },
    [appendThinking, queryClient, sessionId, updateExchange],
  );

  const handlePlanApprove = useCallback(
    async (stream: AsyncGenerator<ChatStreamEvent>) => {
      if (!pendingPlan) return;
      const { exchangeId } = pendingPlan;
      setPendingPlan(null);
      setIsStreaming(true);
      updateExchange(exchangeId, { isStreaming: true });
      await consumeResumeStream(stream, exchangeId, "resume", "Plan resume failed");
    },
    [consumeResumeStream, pendingPlan, updateExchange],
  );

  const handleClarifySubmit = useCallback(
    async (stream: AsyncGenerator<ChatStreamEvent>) => {
      if (!pendingClarification) return;
      const { exchangeId } = pendingClarification;
      setPendingClarification(null);
      setIsStreaming(true);
      updateExchange(exchangeId, { isStreaming: true });
      await consumeResumeStream(stream, exchangeId, "clarify", "Clarify resume failed");
    },
    [consumeResumeStream, pendingClarification, updateExchange],
  );

  return {
    requestedSession,
    sessionId,
    exchanges,
    isStreaming,
    hydrating,
    ask,
    pendingPlan,
    setPendingPlan,
    pendingClarification,
    setPendingClarification,
    handlePlanApprove,
    handleClarifySubmit,
  };
}
